import { useState } from 'react';
import type { AttachmentRotation, InterviewAttachment } from '@app/shared';
import {
  downloadAttachment,
  useDeleteAttachment,
  useSaveAttachmentRotation,
} from './api';
import { ImageLightbox, type LightboxImage } from './ImageLightbox';
import { DesignPromptDialog } from '../design-prompt';
import type { InterviewOwnerType } from '../interviews/api';

interface Props {
  attachments: InterviewAttachment[];
  canWrite: boolean;
  /** 客戶 id 或廠商 id(由 ownerType 決定) */
  ownerId: number;
  ownerType: InterviewOwnerType;
  /** 給「✨ 美編生圖」用,儲存生成圖時要 upload 到的訪談。supplier 模式下美編按鈕暫不顯示。 */
  interviewId: number;
}

const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function isPreviewableImage(mime: string): boolean {
  return IMAGE_MIMES.has(mime);
}

function typeIcon(mime: string): string {
  if (mime.startsWith('image/')) return '📷';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel') || mime === 'text/csv') return '📊';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '📽';
  return '📎';
}

export function AttachmentList({
  attachments,
  canWrite,
  ownerId,
  ownerType,
  interviewId,
}: Props): JSX.Element | null {
  const deleteMutation = useDeleteAttachment(ownerId, ownerType);
  const rotationMutation = useSaveAttachmentRotation(ownerId, ownerType);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // REQ-20260429001:美編 prompt Dialog 目標附件
  const [designTarget, setDesignTarget] = useState<{ uuid: string; originalName: string } | null>(
    null,
  );

  if (!attachments || attachments.length === 0) return null;

  // 同訪談內的圖片附件,供 Lightbox 左右切換。
  // rotation 一併帶進去,放大檢視才能用該圖已存的角度開啟(REQ-20260909004)
  const images: LightboxImage[] = attachments
    .filter((a) => isPreviewableImage(a.mimeType))
    .map((a) => ({
      uuid: a.uuid,
      originalName: a.originalName,
      rotation: a.rotation,
    }));

  const openLightboxForUuid = (uuid: string): void => {
    const i = images.findIndex((img) => img.uuid === uuid);
    if (i >= 0) setLightboxIndex(i);
  };

  return (
    <>
      <ul className="mt-2 grid gap-1 text-sm">
        {attachments.map((a) => {
          const previewable = isPreviewableImage(a.mimeType);
          return (
            <li key={a.uuid} className="flex items-center gap-2 justify-end">
              <span aria-hidden="true">{typeIcon(a.mimeType)}</span>
              {/* 美編生圖目前僅支援客戶模式(廠商暫無此功能) */}
              {previewable && ownerType === 'customer' ? (
                <button
                  type="button"
                  onClick={() =>
                    setDesignTarget({ uuid: a.uuid, originalName: a.originalName })
                  }
                  className="text-xs border rounded px-1.5 py-0.5 text-violet-700 border-violet-300 hover:bg-violet-50"
                  data-testid="attachment-design-btn"
                  title="開啟美編 prompt 產生器"
                >
                  ✨ 美編
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  if (previewable) {
                    openLightboxForUuid(a.uuid);
                    return;
                  }
                  void downloadAttachment(a.uuid, ownerType).catch((err) => {
                    // eslint-disable-next-line no-alert
                    alert(`下載失敗：${err instanceof Error ? err.message : '未知錯誤'}`);
                  });
                }}
                className="text-blue-600 hover:underline truncate max-w-[16rem] text-left"
                data-testid={previewable ? 'attachment-image-link' : 'attachment-download-link'}
              >
                {a.originalName}
              </button>
              <span className="text-slate-400">{humanSize(a.sizeBytes)}</span>
              {a.uploadedBy ? (
                <span className="text-slate-400 text-xs">by {a.uploadedBy.displayName}</span>
              ) : null}
              {canWrite ? (
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm(`刪除「${a.originalName}」？`)) return;
                    deleteMutation.mutate(a.uuid);
                  }}
                  className="text-xs border rounded px-1.5 py-0.5 text-red-600 border-red-300 hover:bg-red-50"
                >
                  刪除
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {lightboxIndex !== null ? (
        <ImageLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          ownerType={ownerType}
          {...(canWrite
            ? {
                // 回存角度沿用刪除那條權限線(canWrite);mutation 成功後
                // invalidate 訪談查詢,新的 rotation 才會回流成「已存角度」
                onSaveRotation: async (
                  image: LightboxImage,
                  rotation: number,
                ): Promise<void> => {
                  await rotationMutation.mutateAsync({
                    uuid: image.uuid,
                    rotation: rotation as AttachmentRotation,
                  });
                },
              }
            : {})}
        />
      ) : null}
      {/* DesignPromptDialog 目前只支援客戶模式 */}
      {designTarget && ownerType === 'customer' ? (
        <DesignPromptDialog
          open={true}
          onClose={() => setDesignTarget(null)}
          attachment={designTarget}
          interviewId={interviewId}
          customerId={ownerId}
        />
      ) : null}
    </>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
